import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import EntityDetailPopup from '../../utility/EntityDetailPopup';
import { Autocomplete, Button, Chip, MenuItem, Stack, TextField, Typography, Box } from '@mui/material';
import apiService from '../../../api/apiService';
import {
  BATCH,
  cropClasses,
  DownArrowIcon,
  HARVEST,
  MetrcLogo,
  metrcTagsClasses,
  miscClasses,
  NEW,
  PLANT,
  popupClasses,
  roomDetailClasses,
  STRAIN,
  taskLevelOptions,
} from '../../utility';
import { useParams } from 'react-router';
import { FeedbackContext } from '../../../App';
import { debounce } from 'lodash';

export default function AttachNewRoutinePopup(props) {
  const { open, onClose, parentValues, selectedSubphase, farm, fetchPhasesData, currDarkThemeClass } = props;
  const feedbackCX = useContext(FeedbackContext);

  const [values, setValues] = useState({
    default_routines: [],
    task_values: [],
    batches: [],
    strains: [],
    routines: [],
    harvests: [],
    routine_level: null,
    cropplan_id: null,
    selected_routine: '',
    selected_items: [],
    selected_subphase: null,
  });
  const [selectedFilter, setSelectedFilter] = useState(null);
  const [selectedItems, setSelectedItems] = useState([]);
  const [selectedPlantTags, setSelectedPlantTags] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [searchMessage, setSearchMessage] = useState('');
  const [searchResults, setSearchResults] = useState([]);

  const { cropplanid } = useParams();

  const titleText = selectedSubphase ? `Attach New Routine to ${selectedSubphase?.name}` : 'Attach New Routine';
  const headerText = parentValues
    ? `Strain Name : ${typeof parentValues === 'object' ? parentValues?.strain_name : ''}`
    : 'Configure your tasks for the routine. Attach general functions to make them really powerful. Customize the way you like.';

  const handleSetValues = useCallback((prop, value) => setValues(state => ({ ...state, [prop]: value })), []);

  // Updated searchPlantTags to work with exactly 5 characters
  const searchPlantTags = useCallback(
    debounce(async (searchTerm) => {
      if (searchTerm.length !== 5) {
        setSearchMessage('Please enter exactly 5 characters to search');
        setSearchResults([]);
        setIsSearching(false);
        return;
      }
  
      setIsSearching(true);
      setSearchMessage('Searching...');
      
      try {
        const response = await apiService.fetchInstance(
          `cropplan-metrc-tag/?search=${encodeURIComponent(searchTerm)}&cropplan=${cropplanid}`
        );
        
        if (response.data && Array.isArray(response.data)) {
          setSearchResults(response.data);
          setSearchMessage(
            response.data.length > 0 
              ? `Found ${response.data.length} matching plant tags` 
              : 'No matching plant tags found'
          );
        } else {
          setSearchResults([]);
          setSearchMessage('No matching plant tags found');
        }
      } catch (error) {
        console.error("Error searching plant tags:", error);
        setSearchMessage('Error searching plant tags');
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    }, 500),
    [cropplanid]
  );

  useEffect(() => {
    const fetchNewRoutineOptions = async () => {
      try {
        if (cropplanid !== NEW && farm) {
          const response = await apiService.fetchInstance(`new/attach-routine/?cropplan=${cropplanid}&farm_id=${farm}`);
          setValues(prev => ({
            ...prev,
            default_routines: response.data.default_routines,
            routines: response.data.routines,
            task_values: response.data.defaulttask,
            batches: response.data.batches,
            strains: response.data.strains,
            harvests: response.data.harvests,
            cropplan_id: cropplanid,
            selected_subphase: selectedSubphase?.id,
            start_date: selectedSubphase?.start_date,
            end_date: selectedSubphase?.end_date,
          }));
        }
      } catch (error) {
        console.error('Error fetching routine options:', error);
      }
    };
    fetchNewRoutineOptions();
  }, [cropplanid, selectedSubphase]);

  const selectedRoutineTaskLevel = useMemo(() => {
    const routineId = values.selected_routine ? Number(values.selected_routine) : null;

    if (!routineId || !values.default_routines || !values.default_routines.length) {
      return 'Unknown';
    }

    const foundRoutine = values.default_routines.find(routine => routine.id === routineId);

    return foundRoutine?.task_level !== undefined ? foundRoutine.task_level : 'Unknown';
  }, [values.selected_routine, values.default_routines]);

  //update values.routine_level when the selected routine changes
  useEffect(() => {
    if (selectedRoutineTaskLevel !== 'Unknown') {
      handleSetValues('routine_level', selectedRoutineTaskLevel);
    }
  }, [selectedRoutineTaskLevel, handleSetValues]);

  useEffect(() => {
    if (selectedRoutineTaskLevel === BATCH && values.batches && values.batches.length > 0) {
      // Select all batches by default
      setSelectedItems(values.batches.map(batch => batch.id));
    } else if (selectedRoutineTaskLevel === STRAIN && values.strains && values.strains.length > 0) {
      // Select all strains by default
      setSelectedItems(values.strains.map(strain => strain.id));
    } else if (selectedRoutineTaskLevel === HARVEST && values.harvests && values.harvests.length > 0) {
      // Select all harvests by default
      setSelectedItems(values.harvests.map(harvest => harvest.id));
    } else if (selectedRoutineTaskLevel === PLANT) {
      // Clear plant selections when switching to PLANT level
      setSelectedPlantTags([]);
      setSearchResults([]);
      setInputValue('');
      setSearchMessage('');
    } else {
      setSelectedItems([]);
    }
  }, [selectedRoutineTaskLevel, values.batches, values.strains, values.harvests]);

  // update selected_items in values state whenever selectedItems or selectedPlantTags change
  useEffect(() => {
    if (selectedRoutineTaskLevel === PLANT) {
      handleSetValues(
        'selected_items',
        selectedPlantTags.map(tag => tag.id)
      );
    } else {
      handleSetValues('selected_items', selectedItems);
    }
  }, [selectedItems, selectedPlantTags, selectedRoutineTaskLevel, handleSetValues]);

  const handleAttachRoutine = async () => {
    try {
      // Ensure we're sending the most up-to-date values
      const dataToSend = {
        ...values,
        selected_items: selectedRoutineTaskLevel === PLANT ? selectedPlantTags.map(tag => tag.id) : selectedItems,
        routine_level: selectedRoutineTaskLevel,
        selected_routine: values.selected_routine,
      };

      const response = await apiService.createInstance('cropplan-attach-routine/', dataToSend);
      if (response.status === 200) {
        fetchPhasesData();
        onClose();
        feedbackCX.setContextValue(true, {
          type: 'success',
          message: 'Routine Attached to the Subphase Successfully.',
        });
      }
    } catch (error) {
      console.error('Error attaching routine:', error);
      feedbackCX.setContextValue(true, {
        type: 'Failure',
        message: 'Error Attaching Routines.',
        autoHideDuration: 3000,
      });
    }
  };

  const handleCreateClick = () => {
    handleAttachRoutine();
  };

  const entityDetailPopupProps = {
    open,
    onClose,
    currDarkThemeClass,
    title: titleText,
    headContentText: headerText,
    modalMaxWidth: '40vw',
    toShowSave: false,
    toShowCreate: false,
    createText: 'Attach New Routine',
    onCreateClick: handleCreateClick,
    saveText: 'Attach Routine',
  };

  const textfieldProps = ({ prop, inputProps = {}, otherTextProps = {} }) => ({
    className: `${popupClasses?.strainEntry?.textField} ${currDarkThemeClass}`,
    autoComplete: 'off',
    fullWidth: true,
    inputProps,
    value: values?.[prop] ?? '',
    onChange: ({ target: { value } }) => handleSetValues(prop, value),
    ...otherTextProps,
  });

  const anrRoutine2Props = {
    prop: 'selected_routine',
    otherTextProps: {
      label: 'Select Routine',
      required: true,
      select: true,
      SelectProps: {
        IconComponent: DownArrowIcon,
      },
    },
  };

  const cropPlanMenuItemProps = {
    className: `${cropClasses?.menuItem} ${currDarkThemeClass}`,
    focusVisibleClassName: miscClasses?.menuItemFocusDark,
  };

  const routinesList = useMemo(() => {
    if (!values?.default_routines || !values?.default_routines.length) {
      return [
        <MenuItem key={-1} value={-1} disabled {...cropPlanMenuItemProps}>
          No Available Routines.
        </MenuItem>,
      ];
    }

    const existingRoutineIds = values?.routines
      .filter(routine => routine.subphase_id === selectedSubphase?.id)
      .map(routine => routine.default_routine);

    const filteredRoutines = values?.default_routines.filter(routine => {
      const isAlreadyAttached = existingRoutineIds.includes(routine.id);
      const matchesTaskLevelFilter = !selectedFilter || routine.task_level === selectedFilter;
      const hasEnoughTasks = routine.task_count >= 1;
      const includesTemplateId10 = routine.task_template_ids?.includes(10);
      const isUserCreated = routine.is_user_created === true;

      const shouldInclude =
        isUserCreated ||
        (!isUserCreated &&
          includesTemplateId10 &&
          (selectedSubphase?.name === 'Serialization' || selectedSubphase?.name === 'Flower'));

      return matchesTaskLevelFilter && !isAlreadyAttached && hasEnoughTasks && shouldInclude;
    });

    if (filteredRoutines.length === 0) {
      return [
        <MenuItem key={-1} value={-1} disabled {...cropPlanMenuItemProps}>
          No Available Routines for this subphase.
        </MenuItem>,
      ];
    }

    return filteredRoutines.map((routine, i) => (
      <MenuItem key={i} value={routine?.id} {...cropPlanMenuItemProps}>
        <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          {routine?.name} {routine?.is_metrc_compliance === true && <MetrcLogo style={{ width: 24, height: 24 }} />}
        </span>
      </MenuItem>
    ));
  }, [values?.default_routines, values?.routines, cropPlanMenuItemProps, selectedFilter, selectedSubphase?.id]);

  const task_value = useMemo(() => {
    const task = taskLevelOptions.find(task => task?.value === selectedRoutineTaskLevel);
    return task ? task?.label : 'None';
  }, [selectedRoutineTaskLevel]);

  const dependent_routine = useMemo(() => {
    const routineId = values.selected_routine ? Number(values.selected_routine) : null;

    if (!routineId || !values.task_values || !values.task_values.length) {
      return 'None';
    }

    const dependent = values.task_values.find(task => task.selected_routine_id === routineId);
    const task_dependent = values.task_values.find(task => task.id === dependent?.default_parent_task);

    return task_dependent ? task_dependent?.name : 'None';
  }, [values.selected_routine, values.task_values]);

  const handleItemSelection = itemId => {
    setSelectedItems(prev => (prev.includes(itemId) ? prev.filter(id => id !== itemId) : [...prev, itemId]));
  };

  const handlePlantTagDelete = tagToDelete => {
    setSelectedPlantTags(selectedPlantTags.filter(tag => tag.id !== tagToDelete.id));
  };

  const getChipStyle = isSelected => ({
    className: `${roomDetailClasses?.chip} ${currDarkThemeClass}`,
    variant: isSelected ? 'filled' : 'outlined',
    style: { margin: '4px' }, // Ensures consistent spacing between chips
  });

  const renderItemChips = () => {
    let items = [];
    let isDisabled = false;

    if (selectedRoutineTaskLevel === BATCH) {
      items = values.batches || [];
      isDisabled = items.length <= 1;
    } else if (selectedRoutineTaskLevel === STRAIN) {
      items = values.strains || [];
      isDisabled = items.length <= 1;
    } else if (selectedRoutineTaskLevel === HARVEST) {
      items = values.harvests || [];
      isDisabled = items.length <= 1;
    } else if (selectedRoutineTaskLevel === PLANT) {
      // Plant handling is done separately
      return null;
    }

    if (items.length === 0) {
      return <Typography style={{ color: 'white' }}>No items available</Typography>;
    }

    return (
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '4px',
          maxWidth: '100%',
          overflow: 'auto',
          maxHeight: '200px',
          padding: '8px',
          borderRadius: '4px',
        }}
      >
        {items.map(item => (
          <Chip
            key={item.id}
            label={item.name || item.strain_name || `Item ${item.id}`}
            onClick={isDisabled ? undefined : () => handleItemSelection(item.id)}
            clickable={!isDisabled}
            disabled={isDisabled}
            {...getChipStyle(selectedItems.includes(item.id))}
          />
        ))}
      </Box>
    );
  };

  const renderPlantTagSelection = () => {
    return (
      <>
        <Autocomplete
          multiple
          id="plant-tags-autocomplete"
          options={searchResults || []}
          value={selectedPlantTags}
          onChange={(event, newValue) => {
            setSelectedPlantTags(newValue);
          }}
          inputValue={inputValue}
          onInputChange={(event, newInputValue) => {
            setInputValue(newInputValue);
            searchPlantTags(newInputValue);
          }}
          getOptionLabel={option => option.metrc_tag_number}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          getOptionDisabled={option => option.status?.includes('Destroyed')}
          renderInput={params => (
            <TextField
              {...params}
              variant="outlined"
              label="Search Plant Tags (Last 5 Characters)"
              className={`${popupClasses?.strainEntry?.textField} ${currDarkThemeClass}`}
              helperText={searchMessage}
              FormHelperTextProps={{
                style: { color: 'rgba(255, 255, 255, 0.7)' }
              }}
            />
          )}
          renderTags={() => null}
          loading={isSearching}
          loadingText="Searching..."
          noOptionsText={inputValue.length !== 5
            ? "Please Enter exactly 5 Characters"
            : "No Matching Plant Tags Found"}
          className={`${popupClasses?.strainEntry?.textField} ${currDarkThemeClass}`}
          renderOption={(props, option) => (
            <li
              {...props}
              style={{
                ...props.style,
                opacity: option.status?.includes('Destroyed') ? 0.5 : 1,
              }}
            >
              {option.metrc_tag_number}
              {option.status?.includes('Destroyed') && ' (Destroyed)'}
              {option.batch_name && ` - ${option.batch_name}`}
            </li>
          )}
        />

        <Typography style={{ marginTop: '10px', marginBottom: '5px', color: 'white' }}>Selected Plant Tags:</Typography>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '4px',
            maxWidth: '100%',
            overflow: 'auto',
            maxHeight: '200px',
            padding: '8px',
            borderRadius: '4px',
          }}
        >
          {selectedPlantTags.length > 0 ? (
            selectedPlantTags.map(tag => (
              <Chip
                key={tag.id}
                label={tag.metrc_tag_number}
                disabled={tag.status?.includes('Destroyed')}
                onDelete={() => handlePlantTagDelete(tag)}
                {...getChipStyle(true)}
              />
            ))
          ) : (
            <Typography style={{ color: 'white' }}>No Plant Tags Selected</Typography>
          )}
        </Box>
      </>
    );
  };

  return (
    <EntityDetailPopup {...entityDetailPopupProps}>
      <TextField {...textfieldProps(anrRoutine2Props)}>{routinesList}</TextField>

      {values?.selected_routine !== null && values?.selected_routine !== '' && (
        <>
          <Typography style={{ padding: 2, color: 'white' }}>Routine Level: {task_value}</Typography>
          <Typography style={{ padding: 2, color: 'white' }}>Dependent Routine: {dependent_routine}</Typography>
        </>
      )}

      {/* Items based on routine level */}
      {selectedRoutineTaskLevel !== 'None' && selectedRoutineTaskLevel !== 'Unknown' && (
        <Stack spacing={2} style={{ padding: '10px', width: '100%' }}>
          <Typography className={`${metrcTagsClasses?.filterTitleTypo} ${currDarkThemeClass}`}>
            Select {task_value}
            {((selectedRoutineTaskLevel === BATCH && values.batches?.length <= 1) ||
              (selectedRoutineTaskLevel === STRAIN && values.strains?.length <= 1) ||
              (selectedRoutineTaskLevel === HARVEST && values.harvests?.length <= 1)) &&
              ' (disabled - only one item available)'}
          </Typography>

          {selectedRoutineTaskLevel === PLANT ? renderPlantTagSelection() : renderItemChips()}
        </Stack>
      )}

      <Button variant="contained" color="primary" onClick={handleCreateClick} 
        disabled={
          !values.selected_routine || 
          (selectedRoutineTaskLevel === PLANT && selectedPlantTags.length === 0) ||
          (selectedRoutineTaskLevel !== PLANT && selectedItems.length === 0)
        }
        style={{ marginTop: '20px' }}
      >
        Attach Routine
      </Button>
    </EntityDetailPopup>
  );
}
